import { useEffect, useRef, useCallback, useState } from 'react';
import { app, db, doc, setDoc } from '../firebase';
import { handleFirestoreWrite } from '../utils/firestoreErrors';
import { Sentry } from '../utils/sentry';

// VAPID key for Web Push. Get it from:
//   Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
// Set as VITE_VAPID_KEY in .env.local (or in your Vercel env vars).
// Falls back to the previous hardcoded value so existing deployments don't break.
const VAPID_KEY = import.meta.env.VITE_VAPID_KEY
  || 'BKHaWlOpIlIN11uyCYuX_NlI8cvzJHfPOYO_tpRR_tleJXPlhzHUqQXw_RGJi9kNTDf2Ayva5308f2LFcxecIjs';

async function saveFCMToken(uid, token) {
  try {
    // fcmToken is sensitive — anyone with it can push to this device.
    // Lives in profiles_private (owner-only read/write).
    await handleFirestoreWrite(
      () => setDoc(doc(db, 'profiles_private', uid), {
        fcmToken: token,
        fcmUpdatedAt: Date.now()
      }, { merge: true }),
      { op: 'saveFCMToken' }
    );
  } catch (err) {
    // Important: previously we just swallowed this. If the token never
    // reaches Firestore, the user gets ZERO push notifications and
    // there's no signal. Now Sentry sees it (handleFirestoreWrite
    // already reports quota/permission). Worst case is non-fatal — the
    // SDK will refresh and retry the token on next launch.
    console.warn('FCM token save failed:', err);
  }
}

async function initFCMToken(uid) {
  if (!VAPID_KEY) return;
  try {
    const { getMessaging, getToken } = await import('firebase/messaging');
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token) await saveFCMToken(uid, token);
  } catch (err) {
    // FCM may fail for legit reasons (browser doesn't support, service
    // worker not registered, VAPID misconfigured). Log so we can tell
    // these apart in Sentry instead of silently dropping all of them.
    if (err?.code !== 'messaging/permission-blocked' &&
        err?.code !== 'messaging/unsupported-browser') {
      try { Sentry.captureException(err, { tags: { op: 'initFCMToken' } }); } catch {}
    }
    console.warn('FCM init failed:', err?.code || err?.message || err);
  }
}

export function useNotifications(uid) {
  // currentPermission as state so React re-renders when it changes
  // (via requestPermission). Also re-checks on tab visibility change
  // so the UI picks up permission revocations done from browser settings.
  const [currentPermission, setCurrentPermission] = useState(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const permissionRef = useRef(currentPermission);

  // Keep ref in sync with state so notifyNewProposal can read it
  // synchronously without taking state as a dep.
  useEffect(() => { permissionRef.current = currentPermission; }, [currentPermission]);

  // Re-check permission when the tab regains focus — covers the case
  // where the user opens browser settings, revokes permission, then
  // comes back to the tab. Without this the UI lies until next reload.
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        const current = Notification.permission;
        if (current !== permissionRef.current) setCurrentPermission(current);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // On mount, if already granted, register FCM token silently
  useEffect(() => {
    if (!uid) return;
    if (currentPermission === 'granted') initFCMToken(uid);
  }, [uid, currentPermission]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    const result = await Notification.requestPermission();
    setCurrentPermission(result);
    if (result === 'granted' && uid) await initFCMToken(uid);
    return result;
  }, [uid]);

  const notifyNewProposal = useCallback((fromName) => {
    if (permissionRef.current !== 'granted') return;
    if (document.visibilityState === 'visible') return; // In-app toast already shown

    try {
      const n = new Notification('¡Nueva propuesta de intercambio! ✨', {
        body: `${fromName} quiere intercambiar láminas contigo`,
        icon: '/vite.svg',
        badge: '/vite.svg',
        tag: 'album-proposal',
        renotify: true,
        silent: false,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch {
      // Notification may be blocked by OS
    }
  }, []);

  const isSupported = typeof Notification !== 'undefined';

  return { isSupported, currentPermission, requestPermission, notifyNewProposal };
}
